import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { fullName, type GulaInstaller, type GulaOrder } from "./gulaTypes";
import { sendInstallerNotice } from "./installerNotify";
import type { OrderAction } from "./OrderCard";
import { SimpleSelect } from "./SimpleSelect";

const CONFIRMED_BY = [
  { value: "packlink", label: "Packlink (seguimiento entregado)" },
  { value: "whatsapp", label: "El cliente por WhatsApp" },
  { value: "phone", label: "El cliente por teléfono" },
];

/**
 * Confirms the client has the unit at home and, by default, emails the
 * installer right away with the client's data, photos and the product sheet.
 */
export const ReceivedDialog = ({
  order,
  installers,
  onAction,
  onClose,
  onSent,
}: {
  order: GulaOrder | null;
  installers: GulaInstaller[];
  onAction: OrderAction;
  onClose: () => void;
  onSent: () => void;
}) => {
  const notify = useNotify();
  const [confirmedBy, setConfirmedBy] = useState("packlink");
  const [installerId, setInstallerId] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order) return;
    setConfirmedBy("packlink");
    setInstallerId(
      order.installer_id == null ? "" : String(order.installer_id),
    );
    setSendNow(true);
  }, [order]);

  if (!order) return null;
  const installer = installers.find((item) => String(item.id) === installerId);
  const canSend = !!installer?.email;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const label = CONFIRMED_BY.find(
        (item) => item.value === confirmedBy,
      )?.label;
      await onAction(order, "received", {
        installer_id: installerId ? Number(installerId) : null,
        received_source: confirmedBy,
        notes: [order.notes, `Recepción confirmada: ${label}`]
          .filter(Boolean)
          .join("\n"),
      });
      if (sendNow && canSend) {
        const result = await sendInstallerNotice(order.id, installerId);
        notify(
          `Aviso enviado a ${result.installer} (${result.to})${result.photos ? ` con ${result.photos} foto(s)` : ""}`,
          { type: "success" },
        );
      } else {
        notify("Marcado como recibido", { type: "success" });
      }
      onSent();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido completar",
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>El cliente ya tiene el equipo</DialogTitle>
          <DialogDescription>{fullName(order)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <SimpleSelect
            label="¿Quién lo ha confirmado?"
            value={confirmedBy}
            onChange={setConfirmedBy}
            options={CONFIRMED_BY}
          />
          <SimpleSelect
            label="Instalador"
            value={installerId}
            onChange={setInstallerId}
            options={installers.map((item) => ({
              value: String(item.id),
              label: `${item.name}${item.email ? "" : " (sin email)"}`,
            }))}
          />
          <div className="flex items-start gap-2">
            <Checkbox
              id="send-now"
              checked={sendNow && canSend}
              disabled={!canSend}
              onCheckedChange={(checked) => setSendNow(checked === true)}
            />
            <Label htmlFor="send-now" className="font-normal leading-snug">
              Enviar ya el aviso al instalador por email: datos del cliente,
              fotos, ficha del equipo y condiciones
            </Label>
          </div>
          {sendNow && canSend && order.requirements_status !== "approved" ? (
            <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
              Ojo: los requisitos de instalación (fotos, desagüe, espacio y
              enchufe) no están aprobados en la ficha. Puedes avisar igualmente:
              el parte se lo indicará al instalador.
            </p>
          ) : null}
          {!canSend ? (
            <p className="text-sm text-destructive">
              {installer
                ? `${installer.name} no tiene email: añádelo en Instaladores.`
                : "Elige el instalador."}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving
              ? "Guardando…"
              : sendNow && canSend
                ? "Confirmar y avisar"
                : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
